import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { HERO } from '@/constants/testIds';
import { LightningBolt } from '@/components/site/LightningBolt';

const HERO_IMG = 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85';

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export const Hero = () => {
    return (
        <section id="home" data-testid={HERO.root} className="relative min-h-screen flex items-center overflow-hidden">
            {/* Cinematic background */}
            <div className="absolute inset-0 z-0">
                <img
                    src={HERO_IMG}
                    alt="Cinematic corporate event stage"
                    className="w-full h-full object-cover scale-110"
                    style={{ filter: 'brightness(0.4) contrast(1.2) saturate(0.9)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/45 to-black" />
                <div className="hero-vignette absolute inset-0" />
                <div className="grain-overlay" />
            </div>

            {/* Vertical rule */}
            <div className="hidden md:block absolute left-10 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent z-10" />
            <div className="hidden md:block absolute right-10 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent z-10" />

            {/* Natural branching lightning bolt — echoes brand mark */}
            <div className="hidden lg:block absolute right-[4%] top-0 h-full w-[35vw] pointer-events-none z-10">
                <LightningBolt className="w-full h-full" opacity={0.38} />
            </div>

            {/* Mobile / tablet lightning bolt */}
            <div className="lg:hidden absolute right-2 top-4 h-[55vh] w-[45vw] pointer-events-none z-10">
                <LightningBolt className="w-full h-full" opacity={0.28} />
            </div>

            {/* Content */}
            <div className="container-lux relative z-20 py-32">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-4xl"
                >
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-px bg-gold" />
                        <span className="eyebrow">Experiential Events & Creative Production Company</span>
                    </div>

                    <h1
                        data-testid={HERO.headline}
                        className="font-display font-light text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mb-6"
                    >
                        Creating
                        <br />
                        <span className="italic font-extralight text-gold">Extraordinary</span>
                        <br />
                        Experiences
                    </h1>

                    <div
                        data-testid={HERO.subheading}
                        className="text-white/70 text-sm sm:text-base tracking-[0.3em] uppercase mb-8 font-light"
                    >
                        Experiential Events &nbsp;•&nbsp; Creative Production &nbsp;•&nbsp; Entertainment
                    </div>

                    <p
                        data-testid={HERO.description}
                        className="text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mb-10 font-light"
                    >
                        We design, produce, and deliver unforgettable corporate events, entertainment, and immersive brand experiences that inspire audiences and create lasting impact.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            data-testid={HERO.ctaStart}
                            onClick={() => scrollTo('contact')}
                            className="btn-primary group"
                        >
                            Start Your Project
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            data-testid={HERO.ctaPortfolio}
                            onClick={() => scrollTo('portfolio')}
                            className="btn-secondary group"
                        >
                            <Play size={12} className="fill-current" />
                            View Portfolio
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* Bottom marquee-like indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="absolute bottom-8 left-0 right-0 z-20"
            >
                <div className="container-lux flex items-end justify-between">
                    <div className="hidden md:flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-white/40">
                        <div className="w-8 h-px bg-white/40" />
                        Scroll to Explore
                    </div>
                    <div className="hidden md:block text-right">
                        <div className="text-[10px] tracking-[0.4em] uppercase text-white/40">Bengaluru</div>
                        <div className="text-[10px] tracking-[0.4em] uppercase text-gold mt-1">Pan India</div>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
